import { db, auth } from '../config/firebase';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { ApiClient } from './api-client.service';

// ──────────────────────────────────────────────────────────────
// Client-side Gmail Service — mirrors the Flutter mobile app.
//
// The app's admin Gmail account is travelez.app@gmail.com.
// Users forward their booking confirmations TO this address.
// We scan this inbox and filter emails FROM the logged-in user.
//
// Auth tokens are stored in Firestore at `app_config/gmail_auth`
// (shared between mobile and web). The mobile app performs the
// initial OAuth and stores a refresh_token. The web app uses
// that refresh_token to obtain fresh access tokens as needed —
// NO per-user OAuth is required on the web side.
// ──────────────────────────────────────────────────────────────

export interface GmailMessage {
    id: string;
    threadId: string;
    snippet: string;
    subject: string;
    from: string;
    to: string;
    date: Date;
    body: string;
    isRead: boolean;
}

export interface RawEmailAttachment {
    filename: string;
    mimeType: string;
    attachmentId: string;
    size: number;
}

export interface RawEmailData {
    id: string;
    subject: string;
    body: string;
    htmlBody?: string;  // Raw HTML version for rendering
    sender: string;
    date: Date;
    attachments?: RawEmailAttachment[];  // File attachments (metadata only; bytes fetched on demand)
}

interface StoredTokens {
    access_token: string;
    refresh_token?: string;
    expiry: string;
    scopes?: string[];
    updated_at?: any;
}

export class GmailService {
    private static GMAIL_SCOPES = [
        'https://www.googleapis.com/auth/gmail.readonly',
        'https://www.googleapis.com/auth/gmail.modify',
    ];

    // Firestore doc path — same as the mobile Flutter app
    private static AUTH_DOC_PATH = 'app_config';
    private static AUTH_DOC_ID = 'gmail_auth';

    // Booking-related keywords (same as mobile app)
    private static BOOKING_KEYWORDS = [
        'booking', 'confirmation', 'reservation', 'hotel', 'apartment',
        'campground', 'campsite', 'airbnb', 'expedia', 'booking.com',
        'hotels.com', 'flight', 'check-in', 'check in', 'itinerary',
        'ticket', 'car rental', 'rental car', 'hertz', 'avis', 'sixt',
        'europcar', 'enterprise',
        // Activities / attractions / tours
        'activity', 'attraction', 'tour', 'museum', 'admission', 'entrance',
        'excursion', 'experience', 'sightseeing', 'getyourguide', 'viator',
        'tiqets', 'klook', 'headout',
    ];

    // ─── Token management (handled server-side by gateway) ──────

    private static async storeTokens(tokens: Partial<StoredTokens>): Promise<void> {
        const docRef = doc(db, this.AUTH_DOC_PATH, this.AUTH_DOC_ID);
        await setDoc(docRef, {
            ...tokens,
            updated_at: Timestamp.now(),
        }, { merge: true });
    }

    private static async loadStoredTokens(): Promise<StoredTokens | null> {
        try {
            const docRef = doc(db, this.AUTH_DOC_PATH, this.AUTH_DOC_ID);
            const snap = await getDoc(docRef);
            if (!snap.exists()) return null;
            const data = snap.data() as StoredTokens;
            if (!data.access_token) return null;
            return data;
        } catch (error) {
            console.warn('⚠️ Could not load Gmail tokens from Firestore:', error);
            return null;
        }
    }

    // ─── Connection status ───────────────────────────────────────

    static async getConnectionStatus(): Promise<{
        isConnected: boolean;
        lastScan?: Date;
    }> {
        try {
            const tokens = await this.loadStoredTokens();
            if (!tokens) return { isConnected: false };

            if (tokens.refresh_token) {
                try {
                    const uid = auth.currentUser?.uid;
                    const processingDoc = uid
                        ? await getDoc(doc(db, 'users', uid, 'app_config', 'email_processing'))
                        : null;
                    const lastScan = processingDoc?.exists()
                        ? processingDoc.data()?.last_processed_time?.toDate?.()
                        : undefined;
                    return { isConnected: true, lastScan };
                } catch {
                    return { isConnected: true };
                }
            }

            return { isConnected: false };
        } catch (error) {
            console.warn('⚠️ Error checking Gmail status:', error);
            return { isConnected: false };
        }
    }

    // ─── Fetch emails via API gateway ────────────────────────────

    /**
     * @param afterDate  only fetch mail newer than this (the sync cursor).
     * @param opts.lookbackDays  used when there is no [afterDate]: bound the
     *   search to the last N days instead of the whole mailbox. This is how a
     *   deep scan re-offers mail the cursor has already moved past.
     */
    static async fetchEmailsForUser(
        userEmail: string,
        afterDate?: Date,
        opts?: { lookbackDays?: number },
    ): Promise<RawEmailData[]> {
        let searchQuery = `from:${userEmail}`;
        if (afterDate) {
            const epochSeconds = Math.floor(afterDate.getTime() / 1000);
            searchQuery += ` after:${epochSeconds}`;
            console.log(`📧 Gmail query with epoch filter: after:${epochSeconds} (= ${afterDate.toISOString()})`);
        } else if (opts?.lookbackDays) {
            searchQuery += ` newer_than:${opts.lookbackDays}d`;
            console.log(`📧 Gmail query with lookback filter: newer_than:${opts.lookbackDays}d`);
        }

        console.log(`📧 Searching Gmail (via gateway) for emails from: ${userEmail}`);

        const searchData = await ApiClient.get<{ messages?: { id: string }[] }>(
            `/gmail/messages?q=${encodeURIComponent(searchQuery)}&maxResults=50`
        );

        const messageIds: string[] = searchData.messages?.map((m) => m.id) || [];
        console.log(`📧 Found ${messageIds.length} emails from ${userEmail}`);

        const emails: RawEmailData[] = [];

        for (const messageId of messageIds) {
            try {
                const messageData = await ApiClient.get<any>(
                    `/gmail/messages/${messageId}?format=full`
                );

                const headers = messageData.payload?.headers || [];
                const subjectHeader = headers.find((h: any) => h.name.toLowerCase() === 'subject');
                const fromHeader = headers.find((h: any) => h.name.toLowerCase() === 'from');

                const subject = subjectHeader?.value || '';
                const sender = fromHeader?.value || '';
                const dateMs = parseInt(messageData.internalDate, 10);
                const date = new Date(dateMs);

                let body = '';
                body = this.extractBodyFromPayload(messageData.payload);
                const htmlBody = this.extractHtmlFromPayload(messageData.payload);
                const attachments = this.extractAttachmentsFromPayload(messageData.payload);

                if (!body) continue;

                emails.push({
                    id: messageId,
                    subject,
                    body,
                    htmlBody: htmlBody || undefined,
                    sender,
                    date,
                    attachments: attachments.length > 0 ? attachments : undefined,
                });
            } catch (error) {
                console.warn(`Failed to fetch message ${messageId}:`, error);
            }
        }

        return emails;
    }

    // ─── Extract raw HTML from email payload ─────────────────────

    private static extractHtmlFromPayload(payload: any): string {
        if (!payload) return '';

        // Direct HTML body
        if (payload.body?.data && payload.mimeType === 'text/html') {
            return this.decodeBase64(payload.body.data);
        }

        // Multipart — find HTML part
        if (payload.parts) {
            for (const part of payload.parts) {
                if (part.mimeType === 'text/html' && part.body?.data) {
                    return this.decodeBase64(part.body.data);
                } else if (part.mimeType?.startsWith('multipart/') && part.parts) {
                    const nested = this.extractHtmlFromPayload(part);
                    if (nested) return nested;
                }
            }
        }

        return '';
    }

    // ─── Extract file attachments (metadata only) ────────────────
    // Walks the MIME tree collecting parts that have a filename and an
    // attachmentId. The bytes are fetched lazily via the gateway endpoint
    // GET /gmail/messages/:id/attachments/:attachmentId when needed.
    private static extractAttachmentsFromPayload(payload: any): RawEmailAttachment[] {
        const result: RawEmailAttachment[] = [];

        const walk = (part: any) => {
            if (!part) return;
            const filename: string = part.filename || '';
            const attachmentId: string | undefined = part.body?.attachmentId;
            if (filename && attachmentId) {
                result.push({
                    filename,
                    mimeType: part.mimeType || 'application/octet-stream',
                    attachmentId,
                    size: part.body?.size || 0,
                });
            }
            if (Array.isArray(part.parts)) {
                for (const child of part.parts) walk(child);
            }
        };

        walk(payload);
        return result;
    }

    // ─── Deep body extraction from multipart messages ────────────

    private static extractBodyFromPayload(payload: any): string {
        if (!payload) return '';

        // Direct body
        if (payload.body?.data) {
            const decoded = this.decodeBase64(payload.body.data);
            if (payload.mimeType === 'text/html') {
                return this.stripHtml(decoded);
            }
            return decoded;
        }

        // Multipart — collect all parts
        if (payload.parts) {
            let textContent = '';
            let htmlContent = '';

            for (const part of payload.parts) {
                if (part.mimeType === 'text/plain' && part.body?.data) {
                    textContent += this.decodeBase64(part.body.data);
                } else if (part.mimeType === 'text/html' && part.body?.data) {
                    htmlContent += this.decodeBase64(part.body.data);
                } else if (part.mimeType?.startsWith('multipart/') && part.parts) {
                    // Recursively handle nested multipart
                    const nested = this.extractBodyFromPayload(part);
                    if (nested) textContent += nested;
                }
            }

            // Prefer plain text; fall back to cleaned HTML
            if (textContent.trim()) return textContent.trim();
            if (htmlContent.trim()) return this.stripHtml(htmlContent);
        }

        return '';
    }

    // ─── Decode base64url to string ──────────────────────────────

    private static decodeBase64(data: string): string {
        try {
            // Decode base64url → raw bytes
            const binary = atob(data.replace(/-/g, '+').replace(/_/g, '/'));
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }
            // Use TextDecoder so multi-byte characters (Hebrew UTF-8, etc.) are
            // decoded correctly instead of being split into two garbage Latin-1 chars
            return new TextDecoder('utf-8').decode(bytes);
        } catch {
            // Last-resort fallback: plain atob (works for ASCII-only content)
            try { return atob(data.replace(/-/g, '+').replace(/_/g, '/')); } catch { return ''; }
        }
    }

    // ─── Strip HTML tags and clean text ──────────────────────────

    static stripHtml(html: string): string {
        // Remove style and script blocks
        let text = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
        text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
        // Convert <br>, <p>, <div>, <tr> to newlines
        text = text.replace(/<br\s*\/?>/gi, '\n');
        text = text.replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n');
        text = text.replace(/<(p|div|tr|li|h[1-6])[^>]*>/gi, '\n');
        // Remove all remaining tags
        text = text.replace(/<[^>]+>/g, ' ');
        // Decode HTML entities
        text = text.replace(/&nbsp;/gi, ' ');
        text = text.replace(/&amp;/gi, '&');
        text = text.replace(/&lt;/gi, '<');
        text = text.replace(/&gt;/gi, '>');
        text = text.replace(/&quot;/gi, '"');
        text = text.replace(/&#39;/gi, "'");
        // Remove bidirectional Unicode markers (Hebrew support)
        text = text.replace(/[\u200E\u200F\u202A-\u202E\u061C\u2066-\u2069]/g, '');
        // Collapse whitespace
        text = text.replace(/[ \t]+/g, ' ');
        text = text.replace(/\n\s*\n/g, '\n');
        return text.trim();
    }

    // ─── Check if email content is booking-related ───────────────

    static isBookingRelated(subject: string, body: string): boolean {
        const combined = `${subject} ${body}`.toLowerCase();
        return this.BOOKING_KEYWORDS.some(keyword => combined.includes(keyword));
    }

    // ─── Disconnect (no-op for admin account) ──────────────────
    // The admin Gmail account is managed via the mobile app.
    // This method is kept for compatibility but does nothing.

    static async disconnect(_userId: string): Promise<void> {
        console.log('ℹ️ Admin Gmail account is managed via the mobile app. No action taken.');
    }

    // ─── Fetch a specific email by ID ────────────────────────────

    static async getEmail(_userId: string, emailId: string): Promise<GmailMessage | null> {
        try {
            const data = await ApiClient.get<any>(
                `/gmail/messages/${emailId}?format=full`
            );

            const headers = data.payload?.headers || [];
            const getHeader = (name: string) =>
                headers.find((h: any) => h.name.toLowerCase() === name.toLowerCase())?.value || '';

            const body = this.extractBodyFromPayload(data.payload);

            return {
                id: data.id,
                threadId: data.threadId,
                snippet: data.snippet || '',
                subject: getHeader('subject'),
                from: getHeader('from'),
                to: getHeader('to'),
                date: new Date(parseInt(data.internalDate, 10)),
                body,
                isRead: !data.labelIds?.includes('UNREAD'),
            };
        } catch (error) {
            console.error('❌ Error fetching email:', error);
            return null;
        }
    }
}

export default GmailService;
